import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { Form, Checkbox, Select } from 'antd';
import { verifyFeatureAccess } from '../../../global/featureFlags';
import ReferralFilters from './ReferralFilters';
import AutoUploadFilters from './AutoUploadFilters';

const checkboxStyle = {
  marginTop: '10px',
  marginLeft: 0,
};

class FilterSettings extends Component {
  constructor(props) {
    super(props);
    const { params } = props.paramTools;
    const departments = Array.isArray(props.departments) ? props.departments : [];
    // Normalize department params by decoding URI components to match raw slugs from departmentOptions
    const normalizeDepartments = (depts) => {
      if (!depts) return [];
      const deptArray = Array.isArray(depts) ? depts : [depts];
      return deptArray.map((d) => {
        try {
          return decodeURIComponent(d);
        } catch (e) {
          return d;
        }
      });
    };

    this.state = {
      showIncludeReferrals: false,
      failed: Object.keys(params).includes('failed'),
      views: Object.keys(params).includes('views') && params.views === '0',
      patients: Object.keys(params).includes('patients'),
      archived: (Object.keys(params).includes('archived') && params.archived === 'true') || false,
      myRecordsOnly: Object.keys(params).includes('myRecordsOnly'),
      myDepartmentOnly: Object.keys(params).includes('myDepartmentOnly'),
      selectedDepartments: normalizeDepartments(params.department),
      recordFilterType: Object.keys(params).includes('myRecordsOnly') ? 'myRecordsOnly' :
                      (Object.keys(params).includes('myDepartmentOnly') ? 'myDepartmentOnly' :
                      (Object.keys(params).includes('myTaggedRecordsOnly') ? 'myTaggedRecordsOnly' : 'all')),
      myTaggedRecordsOnly: Object.keys(params).includes('myTaggedRecordsOnly'),
      completed: Object.keys(params).includes('completed'),
      scheduled: Object.keys(params).includes('scheduled'),
      overdue: Object.keys(params).includes('overdue'),
      unassigned: Object.keys(params).includes('unassigned'),
      unread: Object.keys(params).includes('unread'),
      failedAutoUploaded: Object.keys(params).includes('failedAutoUploaded'),
      document_type: params.document_type || '',
      includeReferrals: Object.keys(params).includes('includeReferrals'),
      formSubmissions: Object.keys(params).includes('formSubmissions'),
      emailSubmissions: Object.keys(params).includes('emailSubmissions'),
    };
    this.departmentOptions = departments.map((department) => ({
      label: department.name,
      value: department.slug,
    }));
  }

  async componentDidMount() {
    const showIncludeReferrals = await verifyFeatureAccess('referral_management') &&
                                await verifyFeatureAccess('hide_referrals');
    const showAutoUploadFilter = await verifyFeatureAccess('manual_upload_emr');

    this.setState({ showIncludeReferrals, showAutoUploadFilter });
  }

  componentWillReceiveProps(nextProps) {
    const { params } = nextProps.paramTools;
    const { patients } = this.state;
    if (patients !== params.patients) {
      this.setState({
        patients: Object.keys(params).includes('patients'),
      });
    }
  }

  updateCheck = (event) => {
    const { paramTools: { params } } = this.props;
    const isInputChecked = event.target.checked;
    const checkboxToUpdate = event.target.value;
    // Generic handler for all filter checkboxes (views, failed, archived,
    // includeReferrals, formSubmissions, emailSubmissions, unassigned,
    // completed, scheduled, overdue, myDepartmentOnly, myRecordsOnly,
    // myTaggedRecordsOnly).
    //
    // "views" must pass a number ?views=0 instead of a boolean so that
    // Rails handles it properly.
    const queryParamValue = (checkboxToUpdate === 'views' ? 0 : isInputChecked);

    // We'll need to mutate this later based on certain conditions, which is why
    // it's declared here
    let updatedState = { [checkboxToUpdate]: isInputChecked };

    // Create a new query params object so we don't mutate the existing one
    let newParams = params;

    // At any given time, only one of "myDepartmentOnly", "myRecordsOnly", and "myTaggedRecordsOnly" should be checked
    //
    // To enforce this, we'll see if one of them is already checked. If that's true,
    // we need to not only set the new checkbox to true, but set the other two already checked to false
    //
    // Determine if one of the three is already selected
    const limitFilter = ['myDepartmentOnly', 'myRecordsOnly', 'myTaggedRecordsOnly'].filter(filter => filter !== checkboxToUpdate);
    const checkedMyOrMyDepartment = limitFilter.length === 2;

    if (checkedMyOrMyDepartment) {
      // For safety, remove all three params, even if one of them we're supposed to update.
      // The update will be handled in a later block
      delete newParams.department;
      delete newParams.myDepartmentOnly;
      delete newParams.myTaggedRecordsOnly;
      delete newParams.myRecordsOnly;
      // Set the old checkboxes to false
      const firstFilterToUncheck = limitFilter[0];
      const secondFilterToUncheck = limitFilter[1];

      updatedState = {
        selectedDepartments: [],
        [firstFilterToUncheck]: false,
        [secondFilterToUncheck]: false,
        ...updatedState,
      };
    }

    // Update URL query params if a checkbox is checked, otherwise delete them
    if (isInputChecked) {
      newParams = {
        [checkboxToUpdate]: queryParamValue,
        ...params,
      };
      if (checkboxToUpdate === 'failedAutoUploaded') delete newParams.notAttemptedAutoUploaded;
    } else delete newParams[checkboxToUpdate];

    return this.updateStateAndQueryRecords(newParams, updatedState);
  }

  handleRecordFilterChange = (value) => {    
    const filterStates = {
      myDepartmentOnly: value === 'myDepartmentOnly',
      myRecordsOnly: value === 'myRecordsOnly',
      myTaggedRecordsOnly: value === 'myTaggedRecordsOnly'
    };
    
    // Update state with new filter values
    this.setState({
      ...filterStates,
      recordFilterType: value
    });
    
    // Handle filter updates based on selection
    if (value === 'all') {
      // Clear both filters when "all" is selected
      this.updateCheck({target: {checked: false, value: 'myDepartmentOnly'}});
      this.updateCheck({target: {checked: false, value: 'myRecordsOnly'}});
      this.updateCheck({target: {checked: false, value: 'myTaggedRecordsOnly'}});
    } else {
      // Set the selected filter
      this.updateCheck({target: {checked: true, value}});
    }
  }

  handleFormFilterCheckBox = (event) => {
    const { paramTools: { params } } = this.props;
    const { document_type: documentType } = this.state;

    let newParams = params;

    const { checked: isInputChecked, value: checkboxToUpdate } = event.target;
    let type;

    // If input is checked type can be form or form,form_submission
    if (isInputChecked) {
      type = !documentType ? checkboxToUpdate : `${documentType},${checkboxToUpdate}`;
    } else {
      // remove type if box is unchecked
      type = ['form_submission', 'form'].filter(filter => filter !== checkboxToUpdate);
      type = documentType.split(',').length === 2 ? type[0] : '';
    }

    // Update URL query params if a checkbox is checked, otherwise delete them
    if (!type) {
      delete newParams.document_type;
    } else {
      newParams = {
        ...newParams,
        document_type: type.split(','),
      };
    }

    return this.updateStateAndQueryRecords(newParams, { document_type: type });
  }

  updateStateAndQueryRecords = (newParams, updatedState) => {
    const { location, paramTools, inboxActions: { getRecords } } = this.props;

    const {
      stringify,
      stringifyOptions,
      push,
    } = paramTools;

    this.setState(updatedState);

    // If there are no query params, default to /app/{mode}
    if (Object.keys(newParams).length === 0) {
      push(location.pathname);
      getRecords('');
      return;
    }

    // Fetch data with new params
    const updatedParams = { ...newParams, page: 1 };
    const query = stringify(updatedParams, stringifyOptions);
    getRecords(query);
  }

  /**
   * @param {Array} selectedOption the data of the object selected from the filter dropdown
   * { key: department, value: department.toLowerCase(), name: department }
   */
  handleDepartmentChange = (selectedValues) => {
    const { paramTools, inboxActions: { getRecords } } = this.props;
    const {
      params,
      stringify,
      stringifyOptions,
      push,
    } = paramTools;
    const selectedDepartments = Array.isArray(selectedValues) ? selectedValues : [];
    // Delete myRecordsOnly if item in dropdown has been selected
    delete params.myRecordsOnly;
    delete params.myDepartmentOnly;
    delete params.myTaggedRecordsOnly;

    if (selectedDepartments.length === 0) {
      delete params.department;
    } else {
      params.department = selectedDepartments;
    }

    this.setState({
      selectedDepartments,
      myDepartmentOnly: false,
      myRecordsOnly: false,
      myTaggedRecordsOnly: false,
      recordFilterType: 'all',
    });

    if (Object.keys(params).length === 0) {
      push(window.location.pathname);
      getRecords('');
      return;
    }

    const newStringifyOptions = {
      ...stringifyOptions,
      encode: false,
    };

    const newParams = { ...params, page: 1 };
    const query = stringify(newParams, newStringifyOptions);
    getRecords(query);
  }

  isPageOneOf = (paths) => {
    const { location } = this.props;
    return paths.some(path => location.pathname.includes(path));
  }

  render() {
    const { departments } = this.props;
    const {
      views,
      failed,
      archived,
      selectedDepartments,
      recordFilterType,
      document_type: documentType,
      myTaggedRecordsOnly,
      completed,
      scheduled,
      overdue,
      includeReferrals,
      formSubmissions,
      emailSubmissions,
      unassigned,
      unread,
      failedAutoUploaded,
    } = this.state;
    const isFormsOnlyChecked = documentType.split(',').includes('form');
    const isFormSubmissionsOnlyChecked = documentType.split(',').includes('form_submission');
    
    const recordFilterOptions = [
      { value: 'all', label: 'All records' },
      { value: 'myDepartmentOnly', label: "My Department's Records" },
      { value: 'myRecordsOnly', label: 'My Records' },
      ...(this.isPageOneOf(['received']) ? [
        { value: 'myTaggedRecordsOnly', label: "Records I'm Tagged In" }
      ] : [])
    ];
    
    return (
      <div>
        {
          !this.isPageOneOf(['assigned', 'referred', 'voice_inbox'])
          && (
          <>
            <Form.Item
              colon={false}
              style={{ display: 'block' }}
              label={<>Filter For:</>}
              className="record-filter-select"
            >
              <Select
                value={recordFilterType}
                onChange={this.handleRecordFilterChange}
                optionLabelProp="label"
                options={recordFilterOptions}
              />
            </Form.Item>
          </>
          )
        }
        {
          this.props && departments && departments.length > 1
          && (
            <Form.Item
              label="Department"
              style={{ marginTop: 10, display: 'block' }}
            >
              <Select
                mode="multiple"
                allowClear
                placeholder="All departments"
                options={this.departmentOptions}
                onChange={this.handleDepartmentChange}
                value={selectedDepartments}
                style={{ width: '100%' }}
              />
            </Form.Item>
          )
        }
        {this.isPageOneOf(['received', 'referred']) && (
          <AutoUploadFilters updateQuery={this.updateStateAndQueryRecords} location={this.props.location} />
        )}
        {
          this.isPageOneOf(['received', 'referred'])
          && (
            <div style={{ marginBottom: '20px' }}>
              <ReferralFilters updateQuery={this.updateStateAndQueryRecords} location={this.props.location} />
            </div>
          )
        }
        {
          this.isPageOneOf(['sent'])
          && (
            <React.Fragment>
              <Checkbox
                checked={views}
                onChange={this.updateCheck}
                value="views"
                defaultChecked={true}
              >
                Show Unviewed Records Only
              </Checkbox>
              <Checkbox
                checked={failed}
                onChange={this.updateCheck}
                value="failed"
                style={checkboxStyle}
                defaultChecked={failed}
              >
                Show Failed Faxes Only
              </Checkbox>
              <br />
            </React.Fragment>
          )
        }
        {
          !this.isPageOneOf(['sent', 'received', 'assigned', 'referred', 'voice_inbox'])
          && (
            <React.Fragment>
              <Checkbox
                checked={archived}
                onChange={this.updateCheck}
                value="archived"
                style={checkboxStyle}
                defaultChecked={archived}
              >
                Show Archived only
              </Checkbox>
            </React.Fragment>
          )
        }
        {this.isPageOneOf(['received']) && this.state.showIncludeReferrals && (
          <Checkbox
            style={checkboxStyle}
            checked={includeReferrals}
            defaultChecked={includeReferrals}
            value="includeReferrals"
            onChange={this.updateCheck}
          >
            Include Referrals
          </Checkbox>
        )}
        { this.isPageOneOf(['received'])
          && (
            <React.Fragment>
              {this.state.showAutoUploadFilter && (
                <>
                  <br />
                  <Checkbox
                    style={checkboxStyle}
                    checked={failedAutoUploaded}
                    value="failedAutoUploaded"
                    onChange={this.updateCheck}
                  >
                    View Failed Auto Uploaded Faxes
                  </Checkbox>
                </>
              )}
              <br />
              <Checkbox
                style={checkboxStyle}
                checked={unread}
                value="unread"
                onChange={this.updateCheck}
              >
                Show Unread Only
              </Checkbox>
              <br />
            </React.Fragment>
          )
        }
        {
          this.isPageOneOf(['received'])
          && (
          <>
            <Checkbox
              checked={formSubmissions}
              onChange={this.updateCheck}
              value="formSubmissions"
              style={checkboxStyle}
              defaultChecked={formSubmissions}
            >
              Show Form Submissions
            </Checkbox>
            <Checkbox
              checked={emailSubmissions}
              onChange={this.updateCheck}
              value="emailSubmissions"
              style={checkboxStyle}
              defaultChecked={emailSubmissions}
            >
              Show Email Submissions
            </Checkbox>
          </>
          )
        }
        {
          this.isPageOneOf(['received', 'referred'])
          && (<Checkbox
            checked={unassigned}
            onChange={this.updateCheck}
            value="unassigned"
            style={checkboxStyle}
            defaultChecked={unassigned}
          >
            Show Unassigned Documents
          </Checkbox>
          )
        }
        {
          this.isPageOneOf(['uploads'])
          && (
            <>
              <Checkbox
                checked={isFormsOnlyChecked}
                onChange={this.handleFormFilterCheckBox}
                value="form"
                style={checkboxStyle}
                defaultChecked={isFormsOnlyChecked}
              >
                Form Templates
              </Checkbox>
              <Checkbox
                checked={isFormSubmissionsOnlyChecked}
                onChange={this.handleFormFilterCheckBox}
                value="form_submission"
                style={checkboxStyle}
                defaultChecked={isFormSubmissionsOnlyChecked}
              >
                Form Submissions
              </Checkbox>
            </>
          )
        }
        {
          this.isPageOneOf(['assigned'])
          && (
            <>
              <Checkbox
                checked={completed}
                onChange={this.updateCheck}
                value="completed"
                style={checkboxStyle}
                defaultChecked={completed}
              >
                Show completed records only
              </Checkbox>
            </>
          )
        }
        {
          this.isPageOneOf(['referred'])
          && (
            <>
              <Checkbox
                checked={scheduled}
                onChange={this.updateCheck}
                value="scheduled"
                style={checkboxStyle}
                defaultChecked={scheduled}
              >
                Show Scheduled Referrals Only
              </Checkbox>
              <Checkbox
                checked={overdue}
                onChange={this.updateCheck}
                value="overdue"
                style={checkboxStyle}
                defaultChecked={overdue}
              >
                Show Overdue Referrals Only
              </Checkbox>
            </>
          )
        }
      </div>
    );
  }
}

FilterSettings.propTypes = {
  departments: PropTypes.arrayOf(PropTypes.object),
  paramTools: PropTypes.shape({
    params: PropTypes.object,
    stringify: PropTypes.func.isRequired,
    stringifyOptions: PropTypes.object.isRequired,
    push: PropTypes.func.isRequired,
  }).isRequired,
  inboxActions: PropTypes.shape({
    getRecords: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.object.isRequired,
};

FilterSettings.defaultProps = {
  departments: [],
};

export default FilterSettings;
